import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import cart from "@/assets/shopping-cart.svg";
import styles from "./customer-actions.module.scss";
import { useServices } from "@/services-context";
import { Gtag } from "@/services/gtag";
import { Url } from "@/helpers/url";
import { useLocation } from "preact-iso";
import { FixButton } from "../fix-button";
import { FixLink, FixLinkTypes } from "../fix-link";
import { ItemButton } from "@/elements-react/menu/ItemButton";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { FixDivider } from "@/elements-react/fix-divider";
import { googleEvent } from "@/helpers/google-events.ts";

export interface CustomerActionsProps {
  type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG;
}

export const CustomerActions: React.FC<CustomerActionsProps> = ({ type }) => {
  const { t } = useTranslation();
  const services = useServices();
  const customerChannel = services?.customerChannel;
  const cartService = services?.cart;
  const router = useLocation();

  const isAuth = customerChannel?.isCustomerAuth?.();

  const signOutAction = useCallback(async () => {
    if (typeof window !== "undefined") {
      Gtag.pushEvent("sign-out", "");
    }
    await customerChannel?.logout?.();
    cartService?.clear?.();
    router.route(Url.to("home-page-with-lang"));
  }, [customerChannel, cartService, router]);

  const renderAuthenticatedCustomerButtons = () => {
    const handleOrdersClick = useCallback(() => {
      Gtag.pushEvent("view-orders", "");
      const ordersLink = typeof window !== "undefined" ? window.location.origin + "/business-v2/orders" : "/business-v2/orders";
      window.location.href = ordersLink;
    }, []);
    return (
      <div className={styles.buttonsContainer}>
        <ItemButton onClick={handleOrdersClick} action="button" title={t("vqdr8f0oym9cmo2z.orders", "Orders")} schema="column" icon={cart} />
        <FixButton size="large" className={styles.signOutBtn} onClick={signOutAction}>
          {t("bp62k3ysuf8r9us6.sign-out", "Sign out")}
        </FixButton>
      </div>
    );
  };

  const handleOnClick = () => {
    googleEvent("sing-in");
    googleEvent("view-orders");
  };

  const renderUnauthenticatedCustomerButtons = () => (
    <div className={styles.buttonsContainer}>
      <FixButton href={Url.to("login-page")} variant="primary" className={classNames("login-btn")} fullWidth={true} onClick={() => googleEvent("sing-in")}>
        {t("7e19h22khwn365zm.sign-in", "Sign in")}
      </FixButton>
      <div className={styles.signUpContainer}>
        <span className={styles["new-customer-text"]}>{t("gjk3cx3llkfb6f9r.new-customer", "New customer?")}</span>
        <FixLink className={styles["sign-up-link"]} href={Url.to("signup-page")} type={FixLinkTypes.TYPE_MD_BLUE} onClick={() => googleEvent("sign-up")}>
          {t("ya79u75dr5ondfid.sign-up", "Sign up")}
        </FixLink>
      </div>
      <FixDivider className={styles.customerActionsDivider} />
      <ItemButton
        href={Url.to("login-page")}
        action="link-router-ignore"
        sub={t("7e19h22khwn365zm.sign-in", "Sign in")}
        title={t("vqdr8f0oym9cmo2z.orders", "Orders")}
        schema="column"
        icon={cart}
        onClick={handleOnClick}
      />
    </div>
  );

  if (type === LayoutStyleTypes.TYPE_SM) {
    return <div className={styles.root}>{isAuth ? renderAuthenticatedCustomerButtons() : renderUnauthenticatedCustomerButtons()}</div>;
  }

  if (type === LayoutStyleTypes.TYPE_LG) {
    return (
      <div className={styles.root}>
        <div className={styles.heading}>{t("1ystg2f5ui8t2310.my-account", "My account")}</div>
        {isAuth ? renderAuthenticatedCustomerButtons() : renderUnauthenticatedCustomerButtons()}
      </div>
    );
  }

  return null;
};

export default CustomerActions;
